import type { World } from './world.js';
import type { EngineConfig, PickupKind } from '../types.js';
import type { EventQueue } from './events.js';
import type { Prng } from '../prng.js';
import { emitHitBurst, emitExplosionBurst } from './effects.js';
import { addScore, trackSwarmKill } from './score.js';
import { onPlayerHit } from './lives.js';
import { maybeDropPickup } from './pickups.js';

/**
 * Circle-vs-circle collision. Tests:
 *  1) player-bullets vs enemies
 *  2) player-bullets vs boss
 *  3) enemy-bullets vs player
 *  4) enemies vs player body
 *  5) pickups vs player
 */
export function updateCollisions(
  world: World,
  _dtMs: number,
  config: EngineConfig,
  rng: Prng,
  events: EventQueue
): void {
  // 1) player bullets vs enemies
  for (const b of world.bullets) {
    if (!b.alive || b.team !== 'player') continue;
    for (const e of world.enemies) {
      if (!e.alive) continue;
      const dx = b.pos.x - e.pos.x;
      const dy = b.pos.y - e.pos.y;
      const rr = b.radius + e.radius;
      if (dx * dx + dy * dy <= rr * rr) {
        e.hp -= b.damage;
        e.flashMs = 80;
        events.push({
          type: 'hit',
          pos: { x: e.pos.x, y: e.pos.y },
          team: 'enemy',
          heavy: false,
        });
        emitHitBurst(world, { x: e.pos.x, y: e.pos.y }, rng, 4);

        // Laser pierce: reduce pierce, die at 0.
        if (b.kind === 'laser') {
          if (b.pierce > 0) {
            b.pierce -= 1;
          } else {
            b.alive = false;
          }
        } else {
          b.alive = false;
        }

        if (e.hp <= 0) {
          e.alive = false;
          addScore(world, e.scoreValue);
          if (e.group) trackSwarmKill(world, e.group);
          events.push({
            type: 'explode',
            pos: { x: e.pos.x, y: e.pos.y },
            magnitude: 1,
          });
          emitExplosionBurst(world, { x: e.pos.x, y: e.pos.y }, rng, 12);
          maybeDropPickup(world, { x: e.pos.x, y: e.pos.y }, config, rng);
        }

        if (!b.alive) break;
      }
    }
  }

  // 2) player bullets vs boss
  if (world.boss && world.boss.alive) {
    const boss = world.boss;
    for (const b of world.bullets) {
      if (!b.alive || b.team !== 'player') continue;
      const dx = b.pos.x - boss.pos.x;
      const dy = b.pos.y - boss.pos.y;
      const rr = b.radius + boss.radius;
      if (dx * dx + dy * dy <= rr * rr) {
        boss.hp -= b.damage;
        boss.flashMs = 80;
        events.push({
          type: 'hit',
          pos: { x: boss.pos.x, y: boss.pos.y },
          team: 'enemy',
          heavy: true,
        });
        world.hitStopMs = Math.max(world.hitStopMs, 40);
        if (b.kind === 'laser') {
          if (b.pierce > 0) b.pierce -= 1;
          else b.alive = false;
        } else {
          b.alive = false;
        }
      }
    }
  }

  // 3) enemy bullets vs player
  for (const player of world.players) {
    if (!player.alive || player.respawnMs > 0) continue;
    if (player.invulnMs > 0) continue;
    for (const b of world.bullets) {
      if (!b.alive || b.team !== 'enemy') continue;
      const dx = b.pos.x - player.pos.x;
      const dy = b.pos.y - player.pos.y;
      const rr = b.radius + player.hitboxRadius;
      if (dx * dx + dy * dy <= rr * rr) {
        b.alive = false;
        onPlayerHit(world, player, events, rng);
        break;
      }
    }
  }

  // 4) enemy body vs player
  for (const player of world.players) {
    if (!player.alive || player.respawnMs > 0) continue;
    if (player.invulnMs > 0) continue;
    for (const e of world.enemies) {
      if (!e.alive) continue;
      const dx = e.pos.x - player.pos.x;
      const dy = e.pos.y - player.pos.y;
      const rr = e.radius + player.hitboxRadius;
      if (dx * dx + dy * dy <= rr * rr) {
        // Kill the enemy (ram) and damage the player.
        e.alive = false;
        addScore(world, e.scoreValue);
        emitExplosionBurst(world, { x: e.pos.x, y: e.pos.y }, rng, 12);
        events.push({
          type: 'explode',
          pos: { x: e.pos.x, y: e.pos.y },
          magnitude: 1,
        });
        onPlayerHit(world, player, events, rng);
        break;
      }
    }
  }

  // 5) pickups vs player (use magnetRadius for absorb)
  for (const player of world.players) {
    if (!player.alive || player.respawnMs > 0) continue;
    for (const pk of world.pickups) {
      if (!pk.alive) continue;
      const dx = pk.pos.x - player.pos.x;
      const dy = pk.pos.y - player.pos.y;
      const rr = pk.radius + player.radius + config.pickups.magnetRadius;
      if (dx * dx + dy * dy <= rr * rr) {
        absorbPickup(world, player, pk, events);
      }
    }
  }

  // Cull off-screen bullets.
  for (const b of world.bullets) {
    if (!b.alive) continue;
    if (
      b.pos.y < -40 ||
      b.pos.y > config.worldHeight + 40 ||
      b.pos.x < -40 ||
      b.pos.x > config.worldWidth + 40
    ) {
      b.alive = false;
    }
  }

  // Cull off-screen pickups.
  for (const pk of world.pickups) {
    if (!pk.alive) continue;
    if (pk.pos.y > config.worldHeight + 40) pk.alive = false;
  }
}

function absorbPickup(
  world: World,
  player: import('./entities.js').Player,
  pk: import('./entities.js').Pickup,
  events: EventQueue
): void {
  const current = getCurrentPickupWeapon(pk);
  pk.alive = false;
  events.push({ type: 'pickup', pos: { x: pk.pos.x, y: pk.pos.y }, weapon: current });

  if (current === 'shield') {
    player.shieldHp = Math.min(player.shieldHp + 1, 3);
    void world;
    return;
  }

  if (current === player.weapon.kind) {
    if (player.weapon.level < 3) {
      player.weapon.level = (player.weapon.level + 1) as 1 | 2 | 3;
    }
  } else {
    player.weapon.kind = current;
  }
}

function getCurrentPickupWeapon(
  pk: import('./entities.js').Pickup
): PickupKind {
  const cycle: PickupKind[] = ['spread', 'laser', 'homing', 'shield'];
  const baseIdx = cycle.indexOf(pk.weaponAtSpawn);
  const step = Math.floor(pk.ageMs / 1800) % cycle.length;
  return cycle[(baseIdx + step + cycle.length) % cycle.length]!;
}
